import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarCheck, CalendarPlus, Loader2, Unplug } from "lucide-react";

export default function GoogleCalendarConnect() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try {
      const r = await api.get("/oauth/calendar/status");
      setStatus(r.data);
    } catch {
      setStatus({ enabled: false, connected: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const g = params.get("gcal");
    if (!g) return;
    if (g === "connected") toast.success("Google Calendar conectado. Tus vacaciones aprobadas se sincronizarán solas.");
    else if (g === "error") toast.error("No se pudo conectar Google Calendar. Inténtalo de nuevo.");
    params.delete("gcal");
    setParams(params, { replace: true });
    load();
    // eslint-disable-next-line
  }, []);

  const connect = async () => {
    setWorking(true);
    try {
      const r = await api.get("/oauth/calendar/authorize-url");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la conexión");
      setWorking(false);
    }
  };

  const disconnect = async () => {
    setWorking(true);
    try {
      await api.post("/oauth/calendar/disconnect");
      toast.success("Google Calendar desconectado");
      await load();
    } catch {
      toast.error("No se pudo desconectar");
    } finally {
      setWorking(false);
    }
  };

  if (loading || !status?.enabled) return null;

  if (status.connected) {
    return (
      <Card className="p-4 flex flex-wrap items-center justify-between gap-3 border-emerald-200 bg-emerald-50/60 dark:bg-emerald-500/10 dark:border-emerald-500/20" data-testid="gcal-connect-card">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center shrink-0">
            <CalendarCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <p className="text-sm font-medium">Google Calendar conectado</p>
            <p className="text-xs text-muted-foreground">
              {status.google_email ? `Como ${status.google_email}. ` : ""}Tus vacaciones aprobadas aparecen en tu calendario.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" disabled={working} onClick={disconnect} data-testid="gcal-disconnect-btn">
          {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unplug className="h-4 w-4" />} Desconectar
        </Button>
      </Card>
    );
  }

  return (
    <Card className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid="gcal-connect-card">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <CalendarPlus className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="text-sm font-medium">Conecta tu Google Calendar</p>
          <p className="text-xs text-muted-foreground">Tus vacaciones aprobadas se añadirán automáticamente a tu calendario de Google.</p>
        </div>
      </div>
      <Button size="sm" className="gap-1.5" disabled={working} onClick={connect} data-testid="gcal-connect-btn">
        {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />} Conectar Google Calendar
      </Button>
    </Card>
  );
}
